<template>
	<section v-if="total > 0" class="user-evaluate">
		<h3 class="section-title">{{ t(`${I18N_NS}.sections.userEvaluate`) }}</h3>
		<n-spin :show="loading">
			<ul class="user-evaluate__grid" role="list">
				<li
					v-for="(item, index) in data"
					:key="item.id"
					:ref="el => bindReviewImpression(el, item, index)"
					class="review-card"
					role="listitem"
				>
					<div class="review-card__head">
						<img
							class="review-card__avatar"
							:src="
								buildOssImageUrl(item.avatar || defaultAvatar, {
									strategy: 'avatar',
									w: 96,
									h: 96,
									q: 80,
								})
							"
							:alt="item.nickname || t(`${I18N_NS}.anonymousUser`)"
						/>
						<div class="review-card__meta">
							<div class="review-card__name-row">
								<span class="review-card__name">
									{{ item.nickname || t(`${I18N_NS}.anonymousUser`) }}
								</span>
								<span class="review-card__stars">
									<img
										v-for="index in STAR_COUNT"
										:key="index"
										:src="index <= item.score ? evaluateIcon : noEvaluateIcon"
										alt=""
										aria-hidden="true"
									/>
									<em>{{ item.score }}</em>
								</span>
							</div>
							<time class="review-card__time">{{ item.create_at }}</time>
						</div>
					</div>
					<p class="review-card__content">{{ item.content }}</p>
				</li>
			</ul>
		</n-spin>

		<n-pagination
			v-if="pageCount > 1"
			v-model:page="page"
			:disabled="loading"
			:page-count="pageCount"
		>
			<template #prev>
				<div class="pagination-btn" :aria-label="t(`${I18N_NS}.prevPage`)">
					<span class="chevron chevron--left" aria-hidden="true"></span>
				</div>
			</template>
			<template #next>
				<div class="pagination-btn" :aria-label="t(`${I18N_NS}.nextPage`)">
					<span class="chevron chevron--right" aria-hidden="true"></span>
				</div>
			</template>
		</n-pagination>
	</section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { getEvaluateList } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { buildOssImageUrl } from "~/utils/oss-image";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultAvatar from "~/assets/images/default_avatar.png";
import evaluateIcon from "~/assets/images/pc/evaluate.png";
import noEvaluateIcon from "~/assets/images/pc/no-evaluate.png";

const I18N_NS = "teacherDetail.pc";
const STAR_COUNT = 5;
const { t } = useI18n();
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	teacherId: {
		type: String,
		default: "",
	},
});
const emit = defineEmits(["has-content-change"]);

const page = ref(1);
const size = ref(6);
const total = ref(0);
const data = ref([]);
const loading = ref(false);

const pageCount = computed(() => Math.ceil(total.value / size.value) || 0);

function bindReviewImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `review_${item.id}`, "mentor_review_impression", {
		review_id: String(item.id),
		rating: item.score,
		position: (page.value - 1) * size.value + index + 1,
	});
}

const getList = async () => {
	if (loading.value || !props.teacherId) return;
	loading.value = true;

	try {
		const res = await getEvaluateList({
			uid: props.teacherId,
			page: page.value,
			size: size.value,
		});

		const list = Array.isArray(res?.data?.list) ? res.data.list : [];
		data.value = list.map(item => ({
			...item,
			score: Math.min(Math.max(Math.ceil(Number(item.score) || 0), 0), STAR_COUNT),
		}));
		total.value = Number(res?.data?.pager?.total_count) || 0;
		emit("has-content-change", total.value > 0);
	} catch {
		total.value = 0;
		data.value = [];
		emit("has-content-change", false);
	} finally {
		loading.value = false;
	}
};

getList();

watch(
	() => page.value,
	(nextPage, prevPage) => {
		if (prevPage != null && nextPage !== prevPage) {
			tracking?.track?.("mentor_review_pagination_click", {
				page_index: nextPage,
				direction: nextPage > prevPage ? "next" : "prev",
			});
		}
		getList();
	}
);

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.section-title {
	margin: 0 0 24px;
	color: #333;
	font-size: 24px;
	font-weight: 500;
	line-height: 1.3;
}

.user-evaluate {
	font-family: "Source Han Serif CN", "Source Han Serif CN";
	margin-top: 15px;

	&__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 18px;
		margin: 0;
		padding: 0;
		list-style: none;
	}
}

.review-card {
	min-width: 0;
	padding: 18px;
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	background: #ffffff;

	&__head {
		display: flex;
		align-items: flex-start;
		gap: 12px;
	}

	&__avatar {
		flex: 0 0 auto;
		width: 42px;
		height: 42px;
		border-radius: 50%;
		object-fit: cover;
		background: #f5f5f5;
	}

	&__meta {
		flex: 1;
		min-width: 0;
	}

	&__name-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	&__name {
		min-width: 0;
		color: #333333;
		font-size: 16px;
		font-weight: 600;
		line-height: 1.35;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__stars {
		display: inline-flex;
		align-items: center;
		gap: 2px;
		color: #e09109;
		font-size: 13px;
		white-space: nowrap;

		img {
			width: 12px;
			height: 12px;
		}

		em {
			margin-left: 4px;
			font-style: normal;
		}
	}

	&__time {
		display: block;
		margin-top: 4px;
		color: #999999;
		font-size: 14px;
		line-height: 1.4;
	}

	&__content {
		margin: 14px 0 0;
		color: #585858;
		font-size: 14px;
		line-height: 1.7;
		@include line-clamp(2);
	}
}

:deep(.n-spin) {
	color: #333333;
}

:deep(.n-pagination) {
	justify-content: center;
	margin-top: 32px;
	--n-item-text-color-hover: #333333 !important;

	.n-pagination-item {
		height: 32px;
		min-width: 32px;
		border: 1px solid #eeeeee !important;
		border-radius: 8px;
		color: #333333;
		white-space: nowrap;

		&:hover {
			background: #e5e5e5 !important;
		}
	}

	.n-pagination-item--button {
		width: 32px;
		min-width: 32px;
		padding: 0;
	}

	.n-pagination-item--disabled {
		border-color: #eeeeee !important;
		background: #eeeeee !important;
		color: #c8c8c8 !important;
		cursor: not-allowed;
	}

	.n-pagination-item--active {
		border-color: #333333 !important;
		background: #333333 !important;
		color: #ffffff !important;

		&:hover {
			opacity: 0.7;
			background: #333333 !important;
		}
	}
}

.pagination-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	color: inherit;
}

.chevron {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;

	&--left {
		margin-left: 3px;
		transform: rotate(-135deg);
	}

	&--right {
		margin-right: 3px;
		transform: rotate(45deg);
	}
}

@media (max-width: 1024px) {
	.user-evaluate {
		&__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
}

@media (max-width: 640px) {
	.user-evaluate {
		&__grid {
			grid-template-columns: 1fr;
		}
	}
}
</style>
